import { Extension, GLTF, ReaderContext, WriterContext, vec3 } from '@gltf-transform/core';
import { KHR_MATERIALS_ANISOTROPY} from '../constants';
import { Anisotropy } from './anisotropy';

const NAME = KHR_MATERIALS_ANISOTROPY;

interface AnisotropyDef {
	anisotropy?: number;
	anisotropyDirection?: vec3;
	anisotropyTexture?: GLTF.ITextureInfo;
	anisotropyDirectionTexture?: GLTF.ITextureInfo;
}

/**
 * # MaterialsAnisotropy
 */
export class MaterialsAnisotropy extends Extension {
	public readonly extensionName = NAME;
	public static readonly EXTENSION_NAME = NAME;

	public createAnisotropy(): Anisotropy {
		return new Anisotropy(this.document.getGraph());
	}

	public read(context: ReaderContext): this {
		const jsonDoc = context.jsonDoc;
		const materialDefs = jsonDoc.json.materials || [];
		const textureDefs = jsonDoc.json.textures || [];
		materialDefs.forEach((materialDef, materialIndex) => {
			if (materialDef.extensions && materialDef.extensions[NAME]) {
				const anisotropy = this.createAnisotropy();
				context.materials[materialIndex].setExtension(NAME, anisotropy);

				const anisotropyDef = materialDef.extensions[NAME] as AnisotropyDef;

				// Factors.

				if (anisotropyDef.anisotropy !== undefined) {
					anisotropy.setAnisotropy(anisotropyDef.anisotropy);
				}
				if (anisotropyDef.anisotropyDirection !== undefined) {
					anisotropy.setAnisotropyDirection(
						anisotropyDef.anisotropyDirection
					);
				}

				// Textures.

				if (anisotropyDef.anisotropyTexture !== undefined) {
					const textureInfoDef = anisotropyDef.anisotropyTexture;
					const texture = context.textures[textureDefs[textureInfoDef.index].source!];
					anisotropy.setAnisotropyTexture(texture);
					context.setTextureInfo(anisotropy.getAnisotropyTextureInfo()!, textureInfoDef);
				}
				if (anisotropyDef.anisotropyDirectionTexture !== undefined) {
					const textureInfoDef = anisotropyDef.anisotropyDirectionTexture;
					const texture = context.textures[textureDefs[textureInfoDef.index].source!];
					anisotropy.setAnisotropyDirectionTexture(texture);
					context.setTextureInfo(
						anisotropy.getAnisotropyDirectionTextureInfo()!,
						textureInfoDef
					);
				}
			}
		});

		return this;
	}

	public write(context: WriterContext): this {
		const jsonDoc = context.jsonDoc;

		this.document.getRoot()
			.listMaterials()
			.forEach((material) => {
				const anisotropy = material.getExtension<Anisotropy>(NAME);
				if (anisotropy) {
					const materialIndex = context.materialIndexMap.get(material)!;
					const materialDef = jsonDoc.json.materials![materialIndex];
					materialDef.extensions = materialDef.extensions || {};

					// Factors.

					const anisotropyDef = materialDef.extensions[NAME] = {
						anisotropy: anisotropy.getAnisotropy(),
						anisotropyDirection: anisotropy.getAnisotropyDirection(),
					} as AnisotropyDef;

					// Textures.

					if (anisotropy.getAnisotropyTexture()) {
						const texture = anisotropy.getAnisotropyTexture()!;
						const textureInfo = anisotropy.getAnisotropyTextureInfo()!;
						anisotropyDef.anisotropyTexture
							= context.createTextureInfoDef(texture, textureInfo);
					}
					if (anisotropy.getAnisotropyDirectionTexture()) {
						const texture = anisotropy.getAnisotropyDirectionTexture()!;
						const textureInfo = anisotropy.getAnisotropyDirectionTextureInfo()!;
						anisotropyDef.anisotropyDirectionTexture
							= context.createTextureInfoDef(texture, textureInfo);
					}
				}
			});

		return this;
	}
}
